import { useMemo, useRef, useState, useEffect } from 'react'

import {
  CaretDownOutlined,
  CaretUpOutlined,
  InfoCircleOutlined,
  ExportOutlined,
  SearchOutlined,
} from '@ant-design/icons'

// material-ui
import {
  useTheme,
  Popper,
  Paper,
  ClickAwayListener,
  CardContent,
  List,
  Checkbox,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Grid,
  Button,
  Typography,
  Tooltip,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material'

// project import
import Transitions from 'components/@extended/Transitions'
import MainCard from 'components/MainCard'
import { useCallback } from 'react'
import { LoadingButton } from '@mui/lab'

const createStyles = (theme, { fullWidth, size, variant }) => ({
  popper: {
    zIndex: 2001,
  },
  paper: {
    boxShadow: theme.customShadows.z1,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.secondary.main,
    width: fullWidth ? '100%' : 'auto',
  },
  cardContent: {
    '&:last-child': {
      p: 0,
    },
    p: 0,
    backgroundColor: theme.palette.grey[200],
  },
  list: {
    overflow: 'auto',
    maxHeight: '400px',
    width: fullWidth ? '100%' : '330px',
    pt: 0,
    mt: 0.25,
  },
  listItemButton: {
    '&.Mui-disabled': {
      pointerEvents: 'auto',
    },
  },
  buttonEndIcon: {
    width: '10px',
    height: '10px',
    padding: 0,
    margin: 0,
  },
  menuButton: {
    height: size === 'small' ? 'auto' : '36px',
    padding: size === 'small' ? '4px 12px' : '5px 15px',
    width: fullWidth ? '100%' : 'auto',
    justifyContent: 'space-between',
    ...(variant === 'form' && {
      borderColor: theme.palette.secondary.darker,
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.text.disabled,
      '&:hover': {
        borderColor: theme.palette.text.secondary,
        backgroundColor: theme.palette.secondary.main,
      },
    }),
  },
  tooltipExclamation: {
    color: theme.palette.grey['A100'],
  },
  applyButton: {
    margin: '8px 16px 16px 16px',
  },
  portfolioDeepLinkIcon: {
    color: theme.palette.primary.darker,
  },
  searchListItem: {
    pt: '16px',
    pb: 0,
  },
})

export const PortfolioSelect = ({
  selectedPortfolios = [],
  onChange,
  onApply,
  portfolios = [],
  portfoliosById = {},
  isLoading = false,
  onPortfolioEdit,
  buttonText = 'Portfolio',
  showApplyButton = true,
  showPortfolioNames = false,
  showSelectedCount = true,
  fullWidth = false,
  error = false,
  size = 'medium',
  disabled = false,
  variant = 'form', // 'form' | 'header'
}) => {
  const theme = useTheme()
  const styles = createStyles(theme, { fullWidth, size, variant })

  const [search, setSearch] = useState('')
  const [isPortfoliosMenuOpen, setIsPortfoliosMenuOpen] = useState(false)

  // Handle both array of portfolio objects and array of portfolio IDs
  const normalizedSelectedPortfolios = useMemo(() => {
    if (!selectedPortfolios || selectedPortfolios.length === 0) return []

    // If it's an array of IDs, convert to portfolio objects
    if (typeof selectedPortfolios[0] === 'string' || typeof selectedPortfolios[0] === 'number') {
      return selectedPortfolios.map((id) => portfoliosById[id]).filter(Boolean)
    }

    // If it's already an array of portfolio objects
    return selectedPortfolios
  }, [selectedPortfolios, portfoliosById])

  const [draftSelectedPortfolios, setDraftSelectedPortfolios] = useState(
    normalizedSelectedPortfolios
  )

  // Update draft when normalizedSelectedPortfolios changes
  useEffect(() => {
    setDraftSelectedPortfolios(normalizedSelectedPortfolios)
  }, [normalizedSelectedPortfolios])

  const anchorRef = useRef(null)

  const allPortfolios = useMemo(
    () => portfolios.filter((portfolio) => portfolio.isCounterparty === false) || [],
    [portfolios]
  )

  const getIntersectedPortfolio = useCallback(
    (id, selectedPortfolios) => {
      const portfolio = portfoliosById[id]
      const portfolioAccountsIds = portfolio?.accountsIds || []

      if (!selectedPortfolios) {
        selectedPortfolios = draftSelectedPortfolios
      }

      const intersectedPortfolio = selectedPortfolios?.find((selectedPortfolio) => {
        if (selectedPortfolio.id === id) return false
        const pAccountsIds =
          allPortfolios.find((p) => p.id === selectedPortfolio.id)?.accountsIds || []
        return pAccountsIds.some((pAccountId) => portfolioAccountsIds.includes(pAccountId))
      })

      return intersectedPortfolio
    },
    [allPortfolios, draftSelectedPortfolios, portfoliosById]
  )

  const onPortfolioSelect = useCallback(
    (id) => {
      let newSelectedPortfolios
      if (draftSelectedPortfolios?.some((selectedPortfolio) => selectedPortfolio.id === id)) {
        newSelectedPortfolios = draftSelectedPortfolios.filter((p) => p.id !== id)
      } else {
        newSelectedPortfolios = [...draftSelectedPortfolios, portfoliosById[id]]
      }
      setDraftSelectedPortfolios(newSelectedPortfolios)

      // If no apply button, trigger onChange immediately
      if (!showApplyButton && onChange) {
        onChange(newSelectedPortfolios)
      }
    },
    [draftSelectedPortfolios, portfoliosById, showApplyButton, onChange]
  )

  const isSelectAllChecked = useMemo(() => {
    const draftSelectedPortfoliosById = draftSelectedPortfolios.reduce((acc, p) => {
      acc[p.id] = p
      return acc
    }, {})

    // if one of portfolios is not selected and not intersected with selected portfolios
    // it means that not all portfolios are selected
    for (const portfolio of allPortfolios) {
      if (!getIntersectedPortfolio(portfolio.id) && !draftSelectedPortfoliosById[portfolio.id]) {
        return false
      }
    }

    return true
  }, [allPortfolios, draftSelectedPortfolios, getIntersectedPortfolio])

  const onSelectAll = useCallback(() => {
    let newSelectedPortfolios
    if (isSelectAllChecked) {
      newSelectedPortfolios = []
    } else {
      newSelectedPortfolios = []
      for (const portfolio of allPortfolios) {
        if (!getIntersectedPortfolio(portfolio.id, newSelectedPortfolios)) {
          newSelectedPortfolios.push(portfolio)
        }
      }
    }

    setDraftSelectedPortfolios(newSelectedPortfolios)

    // If no apply button, trigger onChange immediately
    if (!showApplyButton && onChange) {
      onChange(newSelectedPortfolios)
    }
  }, [allPortfolios, getIntersectedPortfolio, isSelectAllChecked, showApplyButton, onChange])

  const isSelectAllIndeterminate = useMemo(() => {
    return (
      isSelectAllChecked &&
      draftSelectedPortfolios.length > 0 &&
      draftSelectedPortfolios.length < allPortfolios.length
    )
  }, [allPortfolios.length, draftSelectedPortfolios.length, isSelectAllChecked])

  const onMenuToggle = useCallback(
    (open) => {
      if (disabled) return
      setIsPortfoliosMenuOpen(open ?? !isPortfoliosMenuOpen)
      setDraftSelectedPortfolios(normalizedSelectedPortfolios)
    },
    [isPortfoliosMenuOpen, normalizedSelectedPortfolios, disabled]
  )

  const onApplyClick = useCallback(() => {
    if (onApply) {
      onApply(draftSelectedPortfolios.map((p) => p.id))
    }
    if (onChange) {
      onChange(draftSelectedPortfolios)
    }
    onMenuToggle(false)
  }, [draftSelectedPortfolios, onMenuToggle, onApply, onChange])

  const isApplyButtonDisabled = useMemo(() => {
    if (normalizedSelectedPortfolios.length !== draftSelectedPortfolios.length) return false
    return normalizedSelectedPortfolios.every(
      (val, index) => val?.id === draftSelectedPortfolios[index]?.id
    )
  }, [draftSelectedPortfolios, normalizedSelectedPortfolios])

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value)
  }, [])

  const filteredPortfolios = useMemo(() => {
    return allPortfolios.filter((portfolio) =>
      portfolio.name.toLowerCase().includes(search.toLowerCase())
    )
  }, [allPortfolios, search])

  const handlePortfolioEdit = useCallback(
    (id) => {
      if (onPortfolioEdit) {
        onPortfolioEdit(id)
      }
      onMenuToggle(false)
    },
    [onPortfolioEdit, onMenuToggle]
  )

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={onMenuToggle}
        variant="outlined"
        color={error ? 'error' : 'secondary'}
        disabled={disabled}
        sx={{
          ...styles.menuButton,
          textTransform: 'none',
          ...(variant === 'form' &&
            normalizedSelectedPortfolios?.length > 0 && {
              color: theme.palette.text.main,
            }),
          ...(error && {
            borderColor: theme.palette.error.main,
            '&:hover': {
              borderColor: theme.palette.error.dark,
            },
          }),
          '&.Mui-disabled': {
            color: theme.palette.text.secondary,
            borderColor: theme.palette.text.disabled,
            backgroundColor: theme.palette.secondary.darker,
            opacity: 0.7,
          },
        }}
        endIcon={
          isPortfoliosMenuOpen ? (
            <CaretUpOutlined style={styles.buttonEndIcon} />
          ) : (
            <CaretDownOutlined style={styles.buttonEndIcon} />
          )
        }
      >
        <Typography
          variant={size === 'small' ? 'subtitle2' : 'subtitle1'}
          sx={{
            textAlign: 'left',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {showPortfolioNames && normalizedSelectedPortfolios?.length > 0
            ? normalizedSelectedPortfolios.map((p) => p.name).join(', ')
            : showSelectedCount
            ? `${buttonText} (${normalizedSelectedPortfolios?.length})`
            : buttonText}
        </Typography>
      </Button>
      <Popper
        placement="bottom-start"
        open={isPortfoliosMenuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal={false}
        sx={{
          ...styles.popper,
          width: fullWidth ? anchorRef.current?.offsetWidth : 'auto',
        }}
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 0],
              },
            },
            {
              name: 'sameWidth',
              enabled: fullWidth,
              fn: ({ state }) => {
                if (fullWidth && anchorRef.current) {
                  state.styles.popper.width = `${anchorRef.current.offsetWidth}px`
                }
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position="top-right"
            in={isPortfoliosMenuOpen}
            {...TransitionProps}
          >
            <Paper sx={styles.paper}>
              <ClickAwayListener onClickAway={() => onMenuToggle(false)}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={styles.cardContent}>
                    <ListItem sx={styles.searchListItem}>
                      <TextField
                        placeholder={'Search Portfolio'}
                        onChange={handleSearchChange}
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchOutlined />
                            </InputAdornment>
                          ),
                        }}
                        value={search}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemButton onClick={onSelectAll}>
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isSelectAllChecked}
                            indeterminate={isSelectAllIndeterminate}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText>
                          <Grid container alignItems={'center'} columnSpacing={1}>
                            <Grid item>Select All</Grid>
                          </Grid>
                        </ListItemText>
                      </ListItemButton>
                    </ListItem>
                    <Divider />
                    <List sx={styles.list}>
                      {filteredPortfolios.length === 0 && (
                        <Typography variant="body2" align="center" color="textSecondary">
                          No results found
                        </Typography>
                      )}
                      {filteredPortfolios.map(({ id, name }) => (
                        <ListItem key={id} disablePadding>
                          <ListItemButton
                            role={undefined}
                            dense
                            disabled={!!getIntersectedPortfolio(id)}
                            onClick={() => !getIntersectedPortfolio(id) && onPortfolioSelect(id)}
                            sx={styles.listItemButton}
                          >
                            <ListItemIcon>
                              <Checkbox
                                value={id}
                                edge="start"
                                checked={draftSelectedPortfolios.map((p) => p.id)?.includes(id)}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText id={`checkbox-list-label-${id}`}>
                              <Grid container alignItems={'center'} columnSpacing={1}>
                                <Grid item>{name}</Grid>
                                {!!getIntersectedPortfolio(id) && (
                                  <Tooltip
                                    key={id}
                                    title={`Portfolio selection restricted: Overlapping accounts with ${
                                      getIntersectedPortfolio(id)?.name
                                    }`}
                                    placement={'right'}
                                  >
                                    <Grid item>
                                      <InfoCircleOutlined style={styles.tooltipExclamation} />
                                    </Grid>
                                  </Tooltip>
                                )}
                              </Grid>
                            </ListItemText>
                          </ListItemButton>
                          {onPortfolioEdit && (
                            <IconButton
                              sx={styles.portfolioDeepLinkIcon}
                              onClick={() => handlePortfolioEdit(id)}
                            >
                              <ExportOutlined />
                            </IconButton>
                          )}
                        </ListItem>
                      ))}
                    </List>
                    {showApplyButton && (
                      <>
                        <Divider />
                        <ListItem disablePadding>
                          <LoadingButton
                            sx={styles.applyButton}
                            variant="contained"
                            fullWidth
                            disabled={isApplyButtonDisabled}
                            onClick={onApplyClick}
                            loading={isLoading}
                          >
                            Apply
                          </LoadingButton>
                        </ListItem>
                      </>
                    )}
                  </CardContent>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  )
}

export default PortfolioSelect
